import type { EnemyRoleId, WeaponId } from "../content/schemas";
import type { RunInventory, RunPhase, WorldSnapshot } from "./snapshot";
import { createRunProgress, type RunProgress } from "./progression";
import {
  createPilotMeta,
  MAX_PILOT_RANK,
  rpRequiredForRank,
  type PilotMeta,
  type RunHistoryEntry,
} from "./pilot-meta";
import { createDailyOps, utcDateKey, type DailyOpsState } from "./daily-ops";
import {
  createComboState,
  createScoreBreakdown,
  type ComboState,
  type ScoreBreakdown,
} from "./scoring";
import { cloneRunDebriefStats, createRunDebriefStats, type RunDebriefStats } from "./run-debrief";
import { sanitizeWeaponCalibration, type WeaponCalibrationMap } from "./weapon-calibration";
import { isSortieContractId, type SortieContractId } from "./sortie-contracts";

export type { PilotMeta, RunHistoryEntry } from "./pilot-meta";
export type { DailyOpsState } from "./daily-ops";

/** Run save (mid-expedition) vs banked profile are intentionally separate. */

export type RunSave = {
  version: 2;
  seed: number;
  tick: number;
  simTime: number;
  phase: RunPhase;
  frameId: string;
  player: WorldSnapshot["player"];
  runInventory: RunInventory;
  runProgress: RunProgress;
  comboState: ComboState;
  scoreBreakdown: ScoreBreakdown;
  killsByRole: Partial<Record<EnemyRoleId, number>>;
  runSalvageCollected: number;
  debriefStats?: RunDebriefStats;
  carrierX: number;
  carrierY: number;
  onboardingStep: number;
  extractProgress: number;
  enemyCount: number;
  eliteAlive: boolean;
  cosmeticsTrailId: string;
  /** Repeatable challenge consumed from the banked queue when this run launched. */
  activeSortieContract?: SortieContractId | null;
  /** Arena guardian progress for objective-gated contracts. */
  bossArenasCleared?: number;
};

/** Legacy run save shape (pre-progression). */
type RunSaveV1 = Omit<RunSave, "version" | "runProgress"> & { version: 1 };

export type BankedProfile = {
  version: 2;
  salvage: number;
  unlockedFrames: string[];
  unlockedWeapons: WeaponId[];
  cosmetics: string[];
  blueprints: string[];
  runsCompleted: number;
  runsFailed: number;
  selectedFrameId: string;
  selectedTrailId: string;
  /** Weapons equipped for the next launch (subset of unlockedWeapons). */
  selectedWeapons: WeaponId[];
  /** Permanent per-weapon sidegrade tracks; old profiles migrate to an empty map. */
  weaponCalibration: WeaponCalibrationMap;
  /** Paid single-run challenge waiting to be consumed by the next launch. */
  queuedSortieContract: SortieContractId | null;
  pilotMeta: PilotMeta;
  runHistory: RunHistoryEntry[];
  /** First-run Q/F literacy coach completed (or soft-gated). */
  weaponLiteracyCoached: boolean;
  lastRun?: {
    level: number;
    score: number;
    bestStreak: number;
    skillNodes: number;
    outcome: "extracted" | "dead";
  };
  dailyOps: DailyOpsState;
  extractStreak: number;
  bestExtractStreak: number;
  settings: {
    focusFalloff: boolean;
    reducedMotion: boolean;
    showTouchControls: boolean;
    colorblindTelegraphs: boolean;
    /** Combat bed / ambient music loop. Default on. */
    musicEnabled: boolean;
    /** Music bus gain 0–1 when enabled. Default 0.4. */
    musicVolume: number;
    /** SFX bus gain 0–1. Default 1. */
    sfxVolume: number;
    /** Master mute for SFX + music. */
    muted: boolean;
    /**
     * Render quality tier for Three.js presentation.
     * Auto picks High/Medium/Low from device + reducedMotion.
     */
    renderQuality: "auto" | "high" | "medium" | "low";
  };
};

export function createDefaultBankedProfile(): BankedProfile {
  return {
    version: 2,
    salvage: 0,
    unlockedFrames: ["skimmer"],
    unlockedWeapons: ["kinetic_scatter", "arc_lance"],
    cosmetics: ["trail_ember"],
    blueprints: [],
    runsCompleted: 0,
    runsFailed: 0,
    selectedFrameId: "skimmer",
    selectedTrailId: "trail_ember",
    selectedWeapons: ["kinetic_scatter", "arc_lance"],
    weaponCalibration: {},
    queuedSortieContract: null,
    pilotMeta: createPilotMeta(),
    runHistory: [],
    weaponLiteracyCoached: false,
    dailyOps: createDailyOps(utcDateKey()),
    extractStreak: 0,
    bestExtractStreak: 0,
    settings: {
      focusFalloff: true,
      reducedMotion: false,
      showTouchControls: false,
      colorblindTelegraphs: false,
      musicEnabled: true,
      musicVolume: 0.4,
      sfxVolume: 1,
      muted: false,
      renderQuality: "auto",
    },
  };
}

export function migrateBankedProfile(raw: unknown): BankedProfile {
  const defaults = createDefaultBankedProfile();
  if (!raw || typeof raw !== "object") return defaults;
  const parsed = raw as Partial<BankedProfile>;
  const unlockedWeapons = (parsed.unlockedWeapons ?? defaults.unlockedWeapons) as WeaponId[];
  const selectedWeapons = (parsed.selectedWeapons ?? unlockedWeapons.slice(0, 2)).filter((w) =>
    unlockedWeapons.includes(w),
  ) as WeaponId[];
  return {
    ...defaults,
    ...parsed,
    version: 2,
    unlockedFrames: parsed.unlockedFrames?.length ? parsed.unlockedFrames : defaults.unlockedFrames,
    unlockedWeapons,
    cosmetics: parsed.cosmetics?.length ? parsed.cosmetics : defaults.cosmetics,
    blueprints: parsed.blueprints ?? [],
    selectedWeapons: selectedWeapons.length ? selectedWeapons : [...defaults.selectedWeapons],
    weaponCalibration: sanitizeWeaponCalibration(parsed.weaponCalibration),
    queuedSortieContract: isSortieContractId(parsed.queuedSortieContract)
      ? parsed.queuedSortieContract
      : null,
    pilotMeta: migratePilotMeta(parsed.pilotMeta, defaults.pilotMeta),
    runHistory: parsed.runHistory ? [...parsed.runHistory] : [],
    dailyOps:
      parsed.dailyOps && parsed.dailyOps.dateKey && Array.isArray(parsed.dailyOps.ops)
        ? {
            dateKey: parsed.dailyOps.dateKey,
            ops: parsed.dailyOps.ops.map((op) => ({ ...op })),
          }
        : defaults.dailyOps,
    extractStreak: typeof parsed.extractStreak === "number" ? parsed.extractStreak : 0,
    bestExtractStreak: typeof parsed.bestExtractStreak === "number" ? parsed.bestExtractStreak : 0,
    settings: migrateSettings(parsed.settings, defaults.settings),
  };
}

function migratePilotMeta(raw: Partial<PilotMeta> | undefined, defaults: PilotMeta): PilotMeta {
  if (!raw) return createPilotMeta();
  const rank = Math.max(
    1,
    Math.min(
      MAX_PILOT_RANK,
      Number.isFinite(raw.rank) ? Math.floor(raw.rank ?? defaults.rank) : defaults.rank,
    ),
  );
  return {
    ...createPilotMeta(),
    ...raw,
    rank,
    rankPoints:
      typeof raw.rankPoints === "number" && Number.isFinite(raw.rankPoints)
        ? Math.max(0, raw.rankPoints)
        : defaults.rankPoints,
    // Recompute instead of trusting the persisted threshold. Rank 50 saves
    // from the former cap stored zero and must resume on the normal curve.
    rankPointsToNext: rpRequiredForRank(rank),
    branchMastery: { ...createPilotMeta().branchMastery, ...raw.branchMastery },
    titles: [...(raw.titles ?? defaults.titles)],
    achievements: [...(raw.achievements ?? [])],
  };
}

function migrateSettings(
  raw: Partial<BankedProfile["settings"]> | undefined,
  defaults: BankedProfile["settings"],
): BankedProfile["settings"] {
  const merged = { ...defaults, ...raw };
  const musicVol =
    typeof raw?.musicVolume === "number" && Number.isFinite(raw.musicVolume)
      ? raw.musicVolume
      : defaults.musicVolume;
  merged.musicVolume = Math.max(0, Math.min(1, musicVol));
  const sfxVol =
    typeof raw?.sfxVolume === "number" && Number.isFinite(raw.sfxVolume)
      ? raw.sfxVolume
      : defaults.sfxVolume;
  merged.sfxVolume = Math.max(0, Math.min(1, sfxVol));
  const rq = raw?.renderQuality;
  merged.renderQuality =
    rq === "auto" || rq === "high" || rq === "medium" || rq === "low" ? rq : defaults.renderQuality;
  return merged;
}

export function migrateRunSave(raw: unknown): RunSave | null {
  if (!raw || typeof raw !== "object") return null;
  const parsed = raw as Partial<RunSave> & Partial<RunSaveV1>;

  if (parsed.version === 2 && parsed.runProgress && parsed.player && parsed.runInventory) {
    return {
      version: 2,
      seed: parsed.seed!,
      tick: parsed.tick ?? 0,
      simTime: parsed.simTime ?? 0,
      phase: parsed.phase ?? "carrier",
      frameId: parsed.frameId ?? "skimmer",
      player: parsed.player,
      runInventory: parsed.runInventory,
      runProgress: {
        ...createRunProgress(),
        ...parsed.runProgress,
        takenNodes: [...(parsed.runProgress.takenNodes ?? [])],
        weaponMods: (parsed.runProgress.weaponMods ?? []).map((m) => ({ ...m })),
        skillPickOffers: [...(parsed.runProgress.skillPickOffers ?? [])],
        banishedNodes: [...(parsed.runProgress.banishedNodes ?? [])],
        rerollsUsed: parsed.runProgress.rerollsUsed ?? 0,
        bestChain: parsed.runProgress.bestChain ?? 0,
      },
      comboState: parsed.comboState
        ? { ...parsed.comboState, recentTags: [...(parsed.comboState.recentTags ?? [])] }
        : createComboState(),
      scoreBreakdown: parsed.scoreBreakdown ? { ...parsed.scoreBreakdown } : createScoreBreakdown(),
      killsByRole: parsed.killsByRole ? { ...parsed.killsByRole } : {},
      runSalvageCollected: parsed.runSalvageCollected ?? 0,
      debriefStats: parsed.debriefStats
        ? cloneRunDebriefStats(parsed.debriefStats)
        : createRunDebriefStats(),
      carrierX: parsed.carrierX ?? 0,
      carrierY: parsed.carrierY ?? 0,
      onboardingStep: parsed.onboardingStep ?? 0,
      extractProgress: parsed.extractProgress ?? 0,
      enemyCount: parsed.enemyCount ?? 0,
      eliteAlive: parsed.eliteAlive ?? false,
      cosmeticsTrailId: parsed.cosmeticsTrailId ?? "trail_ember",
      activeSortieContract: isSortieContractId(parsed.activeSortieContract)
        ? parsed.activeSortieContract
        : null,
      bossArenasCleared: Math.max(0, Math.floor(parsed.bossArenasCleared ?? 0)),
    };
  }
  if (parsed.version === 1 && parsed.seed != null) {
    return {
      version: 2,
      seed: parsed.seed,
      tick: parsed.tick ?? 0,
      simTime: parsed.simTime ?? 0,
      phase: parsed.phase ?? "carrier",
      frameId: parsed.frameId ?? "skimmer",
      player: parsed.player!,
      runInventory: parsed.runInventory!,
      runProgress: createRunProgress(),
      comboState: createComboState(),
      scoreBreakdown: createScoreBreakdown(),
      killsByRole: {},
      runSalvageCollected: 0,
      debriefStats: createRunDebriefStats(),
      carrierX: parsed.carrierX ?? 0,
      carrierY: parsed.carrierY ?? 0,
      onboardingStep: parsed.onboardingStep ?? 0,
      extractProgress: parsed.extractProgress ?? 0,
      enemyCount: parsed.enemyCount ?? 0,
      eliteAlive: parsed.eliteAlive ?? false,
      cosmeticsTrailId: parsed.cosmeticsTrailId ?? "trail_ember",
      activeSortieContract: null,
      bossArenasCleared: 0,
    };
  }
  return null;
}

export function snapshotToRunSave(
  snap: WorldSnapshot,
  extras: { enemyCount: number; eliteAlive: boolean },
): RunSave {
  return {
    version: 2,
    seed: snap.seed,
    tick: snap.tick,
    simTime: snap.simTime,
    phase: snap.phase,
    frameId: snap.player.frameId,
    player: { ...snap.player },
    runInventory: {
      ...snap.runInventory,
      weapons: [...snap.runInventory.weapons],
      components: [...snap.runInventory.components],
      affixes: [...snap.runInventory.affixes],
    },
    runProgress: {
      ...snap.runProgress,
      takenNodes: [...snap.runProgress.takenNodes],
      weaponMods: (snap.runProgress.weaponMods ?? []).map((m) => ({ ...m })),
      skillPickOffers: [...snap.runProgress.skillPickOffers],
    },
    comboState: { ...snap.combo, recentTags: [...snap.combo.recentTags] },
    scoreBreakdown: { ...snap.scoreBreakdown },
    killsByRole: {},
    runSalvageCollected: snap.runInventory.salvage,
    carrierX: snap.carrierX,
    carrierY: snap.carrierY,
    onboardingStep: snap.onboardingStep,
    extractProgress: snap.extractProgress,
    enemyCount: extras.enemyCount,
    eliteAlive: extras.eliteAlive,
    cosmeticsTrailId: snap.cosmeticsTrailId,
    activeSortieContract: snap.activeSortieContract,
    bossArenasCleared: snap.bossArenasCleared,
  };
}
